import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  PaziaBadge,
  PaziaBox,
  PaziaButton,
  PaziaDateRangeSelect,
  PaziaDateSelect,
  PaziaFormButton,
  PaziaLoader,
} from "components/atoms";
import { ButtonSwitch } from "components/atoms/PaziaButtonSwitch/PaziaButtonSwitch";
import { ConfirmationModal } from "components/molecules";
import { formatDate, useToaster } from "utils";
import { useTrainerAvailabilityAdmin } from "api/hooks/useTrainerAvailabilityAdmin";
import { useTrainerAvailabilityRequests } from "api/hooks/useTrainerAvailabilityRequests";
import { useCreateAvailabilityRequest } from "api/hooks/useCreateAvailabilityRequest";
import { useCancelAvailabilityRequest } from "api/hooks/useCancelAvailabilityRequest";
import { TRAINER_AVAILABILITY_STATUS, TrainerAvailabilityRequestApi } from "api/trainers/trainerAvailabilityApi";
import { TrainerAvailabilityCalendar } from "./TrainerAvailabilityCalendar";

const VIEW_MODE = {
  LIST: "Lista",
  CALENDAR: "Kalendarz",
} as const;

interface TrainerAvailabilityAdminPanelProps {
  trainerId: number;
}

const REQUEST_FORM_NAME = {
  START_DATE: "startDate",
  END_DATE: "endDate",
  DUE_DATE: "dueDate",
  COMMENT: "comment",
} as const;

interface RequestFormValues {
  startDate: Date;
  endDate: Date;
  dueDate?: Date | null;
  comment?: string;
}

const requestSchema = yup.object({
  [REQUEST_FORM_NAME.START_DATE]: yup.date().required("Wymagane"),
  [REQUEST_FORM_NAME.END_DATE]: yup
    .date()
    .required("Wymagane")
    .min(yup.ref(REQUEST_FORM_NAME.START_DATE), "Data zakończenia przed rozpoczęciem"),
  [REQUEST_FORM_NAME.DUE_DATE]: yup.date().nullable(),
  [REQUEST_FORM_NAME.COMMENT]: yup.string(),
});

export const TrainerAvailabilityAdminPanel = ({ trainerId }: TrainerAvailabilityAdminPanelProps) => {
  const { showSuccess, showError } = useToaster();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState<TrainerAvailabilityRequestApi | null>(null);
  const [viewMode, setViewMode] = useState<string>(VIEW_MODE.LIST);

  const { data: availability, isLoading: isAvailabilityLoading } = useTrainerAvailabilityAdmin(trainerId);
  const { data: requests, isLoading: isRequestsLoading } = useTrainerAvailabilityRequests(trainerId);
  const createMutation = useCreateAvailabilityRequest(trainerId);
  const cancelMutation = useCancelAvailabilityRequest(trainerId);

  const methods = useForm<RequestFormValues>({ resolver: yupResolver(requestSchema) });
  const { handleSubmit, reset, register } = methods;

  const onSubmit = async (data: RequestFormValues) => {
    try {
      await createMutation.mutateAsync({
        startDate: data.startDate,
        endDate: data.endDate,
        dueDate: data.dueDate ?? undefined,
        comment: data.comment || undefined,
      });
      showSuccess("Wysłano prośbę o dostępność");
      reset();
      setIsFormOpen(false);
    } catch {
      showError("Nie udało się wysłać prośby o dostępność");
    }
  };

  if (isAvailabilityLoading || isRequestsLoading) return <PaziaLoader />;

  const openRequests = (requests ?? []).filter((r) => !r.isFulfilled);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-semibold text-slate-800 dark:text-slate-100">Oznaczona dostępność</h2>
          <ButtonSwitch
            a={VIEW_MODE.LIST}
            b={VIEW_MODE.CALENDAR}
            value={viewMode}
            onChange={setViewMode}
            className="w-56"
          />
        </div>
        {viewMode === VIEW_MODE.CALENDAR ? (
          <TrainerAvailabilityCalendar
            ranges={availability?.ranges ?? []}
            sessionDays={availability?.sessionDays ?? []}
          />
        ) : (
          <>
            {!availability || availability.ranges.length < 1 ? (
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Trener nie oznaczył jeszcze żadnej dostępności.
              </div>
            ) : (
              <div className="space-y-2">
                {availability.ranges.map((range) => (
                  <div
                    key={range.id}
                    className="flex items-center justify-between rounded-sm border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800"
                  >
                    <div className="flex items-center gap-3">
                      <PaziaBadge
                        color={range.status === TRAINER_AVAILABILITY_STATUS.AVAILABLE ? "emerald" : "amber"}
                        size="xs"
                      >
                        {range.status === TRAINER_AVAILABILITY_STATUS.AVAILABLE ? "Dostępna" : "Niedostępna"}
                      </PaziaBadge>
                      <span className="text-sm text-slate-700 dark:text-slate-200">
                        {formatDate(range.startDate)} – {formatDate(range.endDate)}
                      </span>
                    </div>
                    {range.comment && <span className="text-xs text-slate-400">{range.comment}</span>}
                  </div>
                ))}
              </div>
            )}
            {availability && availability.sessionDays.length > 0 && (
              <div className="mt-3 text-xs text-slate-400 dark:text-slate-500">
                Dni z zaplanowanymi zajęciami: {availability.sessionDays.map((d) => formatDate(d)).join(", ")}
              </div>
            )}
          </>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-semibold text-slate-800 dark:text-slate-100">Prośby o dostępność</h2>
          <PaziaButton title="Poproś o dostępność" type="tertiary" onClick={() => setIsFormOpen((v) => !v)} />
        </div>

        {isFormOpen && (
          <PaziaBox className="mb-3 p-4">
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
                <PaziaDateRangeSelect
                  fromName={REQUEST_FORM_NAME.START_DATE}
                  toName={REQUEST_FORM_NAME.END_DATE}
                  label="Okres, o który prosisz"
                  required
                />
                <PaziaDateSelect name={REQUEST_FORM_NAME.DUE_DATE} label="Termin odpowiedzi (opcjonalnie)" />
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Komentarz (opcjonalnie)</label>
                  <input
                    {...register(REQUEST_FORM_NAME.COMMENT)}
                    className="form-input w-full"
                    placeholder="np. potrzebujemy obsady na wrzesień"
                  />
                </div>
                <div>
                  <PaziaFormButton title="Wyślij" type="primary" allowCleanSubmit />
                </div>
              </form>
            </FormProvider>
          </PaziaBox>
        )}

        {openRequests.length < 1 ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">Brak otwartych próśb.</div>
        ) : (
          <div className="space-y-2">
            {openRequests.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between rounded-sm border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800"
              >
                <div className="text-sm text-slate-700 dark:text-slate-200">
                  {formatDate(r.startDate)} – {formatDate(r.endDate)}
                  {r.dueDate && <span className="text-slate-400"> · do {formatDate(r.dueDate)}</span>}
                  {r.comment && <span className="ml-2 text-xs text-slate-400">{r.comment}</span>}
                </div>
                <PaziaButton title="Anuluj" type="tertiary" onClick={() => setRequestToCancel(r)} />
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={!!requestToCancel}
        title="Anuluj prośbę o dostępność"
        description={`Czy na pewno chcesz anulować prośbę na ${
          requestToCancel ? `${formatDate(requestToCancel.startDate)} – ${formatDate(requestToCancel.endDate)}` : ""
        }?`}
        onCloseRequest={() => setRequestToCancel(null)}
        onCancel={() => setRequestToCancel(null)}
        onConfirm={async () => {
          if (!requestToCancel) return;
          try {
            await cancelMutation.mutateAsync(requestToCancel.id);
          } catch {
            showError("Nie udało się anulować prośby o dostępność");
          } finally {
            setRequestToCancel(null);
          }
        }}
      />
    </div>
  );
};
